/**
 * UI component that allows to upload either an image, or a video file,
 * and shows their previews. It is created as a rapid combination of pre-
 * existing ImageUpload and VideoUpload components. Perhaps, it should replace
 * them down the line (with added option to accept either only images, or only
 * videos, or both).
 */

import {
  type FunctionComponent,
  type ReactNode,
  useMemo,
  useRef,
} from 'react';

import { Link, assertClientSide } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import VimeoVideoPreview from 'components/VimeoVideoPreview';
import { deduceVideoUrlKind, isAllowedFile } from 'utils';

import IconImage from 'assets/images/icon-image-16px.svg';
import IconTrash from 'assets/images/icon-trash-16px.svg';

import IconVideo from 'assets/images/icon-video-48px.svg';

import S from './style.scss';

const ACCEPTED_EXTENSIONS = [
  '.avi',
  '.heic',
  '.jpeg',
  '.jpg',
  '.mov',
  '.mp4',
  '.png',
] as const;

type PropsT = {
  error?: string;

  // TODO: It is currently typed as possibly a "string", because it was
  // inherited from the VideoUpload component, which is partially designed
  // to support the "import from URL" feature (which is not completely implemented
  // there). Thus, at the moment we only use "File | undefined" types, and
  // perhaps should remove the "string" option altogether.
  localMedia: File | string | undefined;

  remoteMedia: {
    isReadyToBePlayed: boolean;
    url: string;
  } | null | string;
  setLocalMedia: (media: File | string | undefined) => void;
};

const ImageOrVideoUpload: FunctionComponent<PropsT> = ({
  error,
  localMedia,
  remoteMedia,
  setLocalMedia,
}) => {
  const mediaInputRef = useRef<HTMLInputElement>(null);

  let ready: boolean;
  let url: string;
  if (typeof remoteMedia === 'string') {
    ready = true;
    url = remoteMedia;
  } else if (remoteMedia) {
    ready = remoteMedia.isReadyToBePlayed;
    ({ url } = remoteMedia);
  } else {
    ready = false;
    url = '';
  }

  const previewOrIcon: ReactNode = useMemo(() => {
    if (localMedia) {
      assertClientSide();

      if (typeof localMedia === 'string') {
        const kind = deduceVideoUrlKind(localMedia);
        if (kind === 'VIMEO') {
          return (
            <VimeoVideoPreview
              placeholder={<IconVideo styleName="icon" />}
              url={localMedia}
            />
          );
        }
      } else if (localMedia instanceof File) {
        if (localMedia.type.startsWith('image/')) {
          return (
            <img
              alt="Cover preview"
              src={URL.createObjectURL(localMedia)}
              styleName="imagePreview"
            />
          );
        }
        if (localMedia.type.startsWith('video/')) {
          const objUrl = URL.createObjectURL(localMedia);
          return (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video controls key={objUrl} styleName="videoPreview">
              <source src={objUrl} />
            </video>
          );
        }
      }
    } else if (url) {
      if (deduceVideoUrlKind(url) === 'VIMEO') {
        const placeholder = (
          <div styleName="uploadedAndIsBeingProcessed">
            <Throbber />
            {ready ? null : <div>Uploaded and is being processed</div>}
          </div>
        );

        // TODO: If somebody really pays attention, there is a slightly visible
        // artifact - the throbber position is reset when the video status changes
        // to ready to play, and the original placeholder is replaced by the same
        // placeholder rendered via the preview. Not a big deal, but if we are
        // pedantic, we should render <VimeoVideoPreview> right away, updating it
        // for the possibility to get no URL (and show the placeholder then),
        // and then mounting the player once it gets the URL (so that there is
        // no need to re-mount the placeholder while the player is being prepped).
        return ready
          ? <VimeoVideoPreview placeholder={placeholder} url={url} />
          : placeholder;
      }

      // Otherwise we assume `url` points to an image.
      return (
        <div styleName="remoteImagePreviewWrapper">
          <div styleName="throbber"><Throbber /></div>
          <img
            alt="Cover preview"
            src={url}
            styleName="imagePreview"
          />
        </div>
      );
    }

    return (
      <div styleName="iconsBlock">
        <IconImage styleName="icon" />
        or
        <IconVideo styleName="icon" />
      </div>
    );
  }, [localMedia, ready, url]);

  let containerClassName = '';
  if (error !== undefined) containerClassName += ` ${S.error}`;

  return (
    <div className={containerClassName}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const [media] = e.dataTransfer.files;
          if (media) {
            const allowed = isAllowedFile(media, ['IMAGE', 'VIDEO']);
            if (!allowed.value) {
              // eslint-disable-next-line no-alert
              alert(allowed.reason);
              return;
            }
            setLocalMedia(media);
          }
        }}
        styleName="dropArea"
      >
        {localMedia ? (
          <Link
            onClick={() => {
              if (mediaInputRef.current) mediaInputRef.current.value = '';
              setLocalMedia(undefined);
            }}
            styleName="remove"
            to=""
          >
            <IconTrash />
          </Link>
        ) : null}
        {previewOrIcon}
        <p styleName="p1">Drag and drop your image or video file here</p>
        <p styleName="p2">
          Or &zwnj;
          <Link
            onClick={() => {
              mediaInputRef.current?.click();
            }}
            to=""
          >
            click here
          </Link>
          &zwnj; to search the file in your computer
        </p>
        <p styleName="p3">
          For best results use JPG, PNG, or HEIC image with 1024 &times; 768
          pixels resolution;<br /> or MP4 or MOV video in HD resolution (1920
          &times; 1080 pixels).
        </p>
        <input
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={(e) => {
            const media = e.target.files?.[0];
            if (media) {
              const allowed = isAllowedFile(media, ['IMAGE', 'VIDEO']);
              if (!allowed.value) {
                // eslint-disable-next-line no-alert
                alert(allowed.reason);
                return;
              }

              setLocalMedia(media);
            }
          }}
          ref={mediaInputRef}
          styleName="hidden"
          type="file"
        />
      </div>
      {error ? <div styleName="errorMessage">{error}</div> : null}
    </div>
  );
};

export default ImageOrVideoUpload;
